/**
 * Helpers for ShipmentTrackingEventType codes and API fields
 * (event_type, event_type_name, event_type_name_ar).
 */

import i18n from '../../../i18n';
import { resolveAppLanguage } from '../../../i18n/language';

/**
 * Localized label from ui.eventTypeCodes.* when the key exists; otherwise ''.
 */
function translatedEventTypeCodeLabel(code, i18nLanguage) {
  if (!code) return '';
  const key = `eventTypeCodes.${code}`;
  const lng = resolveAppLanguage(i18nLanguage ?? i18n.language);
  const translated = i18n.t(key, { ns: 'ui', lng, defaultValue: '' });
  // i18n may echo the key when missing even with defaultValue in some configs
  if (!translated || translated === key) return '';
  return translated;
}

/**
 * Display name from GET /tracking_event_type/ catalog row ({ name, name_ar, code|value }).
 * ar → name_ar, then i18n, then English name
 * zh → i18n, then English name (API has no Chinese field)
 * en → English name, then i18n
 */
export function getTrackingEventTypeCatalogDisplayName(row, i18nLanguage) {
  if (!row) return '';
  const name = row.name != null ? String(row.name).trim() : '';
  const nameAr = row.name_ar != null ? String(row.name_ar).trim() : '';
  const code =
    row.code != null
      ? String(row.code).trim()
      : row.value != null
        ? String(row.value).trim()
        : '';
  const lang = resolveAppLanguage(i18nLanguage ?? i18n.language);
  const i18nLabel = translatedEventTypeCodeLabel(code, lang);

  if (lang === 'ar') return nameAr || i18nLabel || name || code;
  if (lang === 'zh') return i18nLabel || name || code;
  return name || i18nLabel || code;
}

/** Autocomplete / list: "Label (code)" */
export function formatTrackingEventTypeCatalogLabelWithCode(row, i18nLanguage) {
  if (!row || typeof row !== 'object') return '';
  const display = getTrackingEventTypeCatalogDisplayName(row, i18nLanguage);
  const code = row.code != null ? String(row.code).trim() : '';
  return code ? `${display} (${code})` : display;
}

/** Fallback English labels when catalog or name fields are unavailable (matches backend codes). */
export const EVENT_TYPE_CODE_LABELS = {
  order_confirmed: 'Order confirmed',
  awaiting_pickup: 'Awaiting pickup',
  picked_up_origin: 'Picked up – origin',
  at_warehouse_origin: 'At warehouse – origin',
  departed_origin_port: 'Departed origin – port',
  departed_origin_airport: 'Departed origin – airport',
  in_transit: 'In transit',
  arrived_destination_port: 'Arrived destination – port',
  arrived_destination_airport: 'Arrived destination – airport',
  departed_destination_airport: 'Departed destination – airport',
  customs_cleared_destination: 'Customs cleared – destination',
  at_dc_destination: 'At distribution center – destination',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  other: 'Other',
};

/**
 * Display title for a customer timeline row (prefers server names, then Arabic when locale is Arabic).
 * @param {object} evt - tracking event with optional event_type_name, event_type_name_ar, event_type
 */
export function getTimelineEventTitle(evt) {
  if (!evt) return i18n.t('eventTypeCodes.update', { ns: 'ui' });
  if (i18n.language?.startsWith('ar') && evt.event_type_name_ar) {
    return evt.event_type_name_ar;
  }
  try {
    const lang = typeof navigator !== 'undefined' && navigator.language
      ? navigator.language.toLowerCase()
      : '';
    if (lang.startsWith('ar') && evt.event_type_name_ar) {
      return evt.event_type_name_ar;
    }
  } catch (_) {
    /* ignore */
  }
  if (i18n.language === 'zh' && evt.event_type) {
    return labelForEventTypeCode(evt.event_type, null);
  }
  if (evt.event_type_name) return evt.event_type_name;
  const code = evt.event_type;
  if (!code) return i18n.t('eventTypeCodes.update', { ns: 'ui' });
  return labelForEventTypeCode(code, null);
}

/**
 * Hide status chip when it repeats the same text as the event title (e.g. Delivered + Delivered).
 */
export function isTimelineStatusChipRedundant(evt, eventTitle, statusLabel) {
  if (!evt?.status_snapshot || !statusLabel) return true;
  const chipLabel = statusLabel(evt.status_snapshot);
  return (eventTitle || '').trim().toLowerCase() === String(chipLabel || '').trim().toLowerCase();
}

/**
 * Label for a raw event_type code using optional catalog options { value, label }[].
 */
export function labelForEventTypeCode(code, selectOptions) {
  if (code == null || code === '') return '—';
  const opt = selectOptions && selectOptions.find((o) => o.value === code);
  if (opt) return opt.label;
  const fallback = EVENT_TYPE_CODE_LABELS[code] || String(code).replace(/_/g, ' ');
  return i18n.t(`eventTypeCodes.${code}`, { ns: 'ui', defaultValue: fallback });
}

/**
 * Grid/row label: prefer nested name fields from serializer, then catalog, then code.
 */
export function eventTypeDisplayFromRow(row, selectOptions) {
  if (!row) return '—';
  if (i18n.language?.startsWith('ar') && row.event_type_name_ar) {
    return row.event_type_name_ar;
  }
  if (i18n.language === 'zh' && row.event_type) {
    return labelForEventTypeCode(row.event_type, selectOptions);
  }
  if (row.event_type_name) return row.event_type_name;
  return labelForEventTypeCode(row.event_type, selectOptions);
}

/** Convert API ISO datetime to value for `<input type="datetime-local" />`. */
export function toDatetimeLocalValue(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return '';
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  } catch {
    return '';
  }
}

/** Convert datetime-local input value to UTC ISO 8601 for API requests. */
export function datetimeLocalToIso(localValue) {
  if (!localValue) return undefined;
  try {
    const d = new Date(localValue);
    if (Number.isNaN(d.getTime())) return undefined;
    return d.toISOString();
  } catch {
    return undefined;
  }
}

/**
 * For PATCH/PUT updates: include `timestamp` only when the operator changed it.
 * Sending `timestamp` (even unchanged) triggers latest-event parent re-sync on the server.
 */
export function buildTimestampUpdateField(originalIso, localValue) {
  const iso = datetimeLocalToIso(localValue);
  if (!iso) return undefined;
  if (originalIso) {
    const orig = new Date(originalIso);
    const next = new Date(iso);
    if (!Number.isNaN(orig.getTime()) && !Number.isNaN(next.getTime()) && orig.getTime() === next.getTime()) {
      return undefined;
    }
  }
  return iso;
}
